/**
 * Typed errors for the browser scrub/upload pipeline.
 *
 * These are CLIENT-side domain errors surfaced to the upload UI — not HTTP
 * errors thrown across the API boundary — so they are plain `Error` subclasses
 * (not `AppError`, which carries an HTTP `statusCode`). The UI matches on the
 * class to render a recoverable choice rather than a crash.
 */

/**
 * The selected `.xlsx` is over `XLSX_SIZE_GUARD_BYTES`. SheetJS cannot stream a
 * partial slice — it materializes the whole workbook — so a large workbook is
 * nudged to CSV before it ever reaches the parser. Recoverable: the UI prompts
 * the user to re-export as CSV.
 */
export class XlsxTooLargeError extends Error {
  /** The offending file's size in bytes. */
  readonly fileSize: number;
  /** The guard threshold that was exceeded. */
  readonly limit: number;

  constructor(fileSize: number, limit: number) {
    super(
      `This .xlsx is large (${fileSize} bytes, limit ${limit}). ` +
        'Re-export it as CSV for a faster, lower-memory upload.',
    );
    this.name = 'XlsxTooLargeError';
    this.fileSize = fileSize;
    this.limit = limit;
  }
}

/**
 * The file's live header set diverged from the `ScrubPlan` built during
 * preflight — the user edited or re-selected a different file between confirming
 * the plan and running the scrub. Aborting here prevents uploading a mis-scrubbed
 * file (e.g. HMAC'ing the wrong column). Recoverable: the UI prompts a re-run of
 * preflight.
 */
export class HeaderDriftError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'HeaderDriftError';
  }
}
